import React from "react";
import {
  ThemedSiderV2,
} from "@refinedev/mui";
import { Box, Divider, Typography, IconButton, Tooltip } from "@mui/material";
import { ChevronRight } from "lucide-react";
import { SiderFooter } from "./SiderFooter";
import { useTranslation } from "react-i18next";
import { useSidebarCollapsed } from "../../hooks/useSidebarCollapsed";
import { useAuth } from "../../contexts/auth-context";

export const CustomSider: React.FC<React.ComponentProps<typeof ThemedSiderV2>> = (props) => {
  const { t } = useTranslation();
  const { setCollapsed } = useSidebarCollapsed();
  const { user } = useAuth();
  const isOperator = user?.role === 'OPERATOR';

  const handleExpandSidebar = () => {
    setCollapsed(false);
  };

  // Effect to manage collapsed state CSS class
  React.useEffect(() => {
    let pendingTimeout: ReturnType<typeof setTimeout> | null = null;

    const checkCollapsedState = () => {
      const drawer = document.querySelector('.MuiDrawer-root');
      if (drawer) {
        const drawerPaper = drawer.querySelector('.MuiDrawer-paper');
        if (drawerPaper) {
          const width = drawerPaper.getBoundingClientRect().width;
          const collapsed = width < 120;

          if (collapsed) {
            drawerPaper.classList.add('sidebar-collapsed');
          } else {
            drawerPaper.classList.remove('sidebar-collapsed');
          }
        }
      }
    };

    const debouncedCheck = () => {
      if (pendingTimeout) clearTimeout(pendingTimeout);
      pendingTimeout = setTimeout(checkCollapsedState, 50);
    };

    // Initial check with delay to ensure DOM is ready
    pendingTimeout = setTimeout(checkCollapsedState, 100);

    // Check on resize and mutations (debounced)
    const observer = new MutationObserver(debouncedCheck);

    const drawer = document.querySelector('.MuiDrawer-root');
    if (drawer) {
      observer.observe(drawer, {
        attributes: true,
        attributeFilter: ['style', 'class'],
        subtree: true,
        childList: true
      });
    }

    window.addEventListener('resize', debouncedCheck);

    return () => {
      if (pendingTimeout) clearTimeout(pendingTimeout);
      observer.disconnect();
      window.removeEventListener('resize', debouncedCheck);
    };
  }, []);

  return (
    <ThemedSiderV2
      {...props}
      render={({ items, logout, collapsed }) => {
        // Filtrer les items cachés (ex: profile, accessible via le menu utilisateur)
        const hiddenResources = ['profile'];
        const visibleItems = items.filter((item: any) => {
          const key = item.key?.toString() || '';
          return !hiddenResources.some(name => key.includes(`/${name}`));
        });

        // Séparer les items en deux catégories (Principal / Gestion)
        // Le point de split est le premier item "gestion" (users, company, etc.)
        const managementResources = ['users', 'company', 'safe-events', 'promo-codes', 'premium-devices', 'ota', 'telesurveilleur'];
        const splitIndex = visibleItems.findIndex((item: any) => {
          const key = item.key?.toString() || '';
          return managementResources.some(name => key.includes(`/${name}`));
        });
        const mainMenuItems = splitIndex > 0 ? visibleItems.slice(0, splitIndex) : visibleItems;
        const managementMenuItems = splitIndex > 0 ? visibleItems.slice(splitIndex) : [];

        return (
          <Box sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            // Sur mobile, assurer une hauteur appropriée
            '@media (max-width: 768px)': {
              height: '100%',
              maxHeight: '100%',
            }
          }}>
            {/* Bouton pour agrandir la sidebar quand elle est réduite */}
            {collapsed && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 1.5,
                  borderBottom: 1,
                  borderColor: 'divider',
                  // Masquer sur mobile car on utilise le drawer
                  '@media (max-width: 768px)': {
                    display: 'none',
                  },
                }}
              >
                <Tooltip title={t('sidebar.expand', 'Agrandir la sidebar')} placement="right">
                  <IconButton
                    onClick={handleExpandSidebar}
                    size="small"
                    sx={{
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'primary.main',
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <ChevronRight size={20} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            <Box
              sx={{
                flex: "1 1 auto",
                overflow: "auto",
                overflowX: "hidden",
                minHeight: 0, // Important pour permettre le shrink du conteneur
                // Appliquer des styles spécifiques pour le mode collapsed
                '& .MuiList-root .MuiListItemButton-root': collapsed ? {
                  justifyContent: 'center !important',
                  px: '12px !important',
                  minWidth: '48px !important',
                  '& .MuiListItemIcon-root': {
                    minWidth: 'auto !important',
                    mr: '0 !important',
                  },
                  '& .MuiListItemText-root': {
                    display: 'none !important',
                  },
                } : {},
              }}
            >
              {/* Première catégorie - Menu principal */}
              <Box sx={{ mb: 2 }}>
                {!collapsed && managementMenuItems.length > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      px: 2,
                      py: 1,
                      display: 'block',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      letterSpacing: '0.5px'
                    }}
                  >
                    {t('sidebar.sections.principal')}
                  </Typography>
                )}
                {mainMenuItems}
              </Box>

              {/* Divider - only show if management section has items */}
              {managementMenuItems.length > 0 && (
                <Divider sx={{ mx: 2, my: 1 }} />
              )}

              {/* Deuxième catégorie - Gestion */}
              {managementMenuItems.length > 0 && (
                <Box>
                  {!collapsed && (
                    <Typography
                      variant="caption"
                      sx={{
                        px: 2,
                        py: 1,
                        display: 'block',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        color: 'text.secondary',
                        fontSize: '0.7rem',
                        letterSpacing: '0.5px'
                      }}
                    >
                      {t('sidebar.sections.management')}
                    </Typography>
                  )}
                  {managementMenuItems}
                </Box>
              )}
            </Box>
            <Box sx={{
              flexShrink: 0,
              mt: 'auto', // Pousser le footer vers le bas
              // Sur mobile, ajouter un padding pour la safe area iOS
              '@media (max-width: 768px)': {
                paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              }
            }}>
              <SiderFooter collapsed={collapsed} />
            </Box>
          </Box>
        );
      }}
    />
  );
};
